import graphql from 'babel-plugin-relay/macro'
import Loading from 'components/Loading'
import useEffectAfterMount from 'hooks/useEffectAfterMount'
import useTableSort from 'hooks/useTableSort'
import React, { useState } from 'react'
import { createPaginationContainer } from 'react-relay'
import { withRouter } from 'react-router-dom'
import { Table } from 'semantic-ui-react'

import Filters from 'components/Filters'
import SearchBar from 'components/SearchBar'
import { EquipmentStatusLabel, EquipmentTypeLabel } from './EquipmentLabels'
import BrandSelect from 'core/brand/components/BrandSelect'
import PropertySelect from 'core/property/components/PropertySelect'
import { getEquipmentTypeText } from './EquipmentConstants'
import { CREATE_EQUIPMENT } from 'config/permissions'
import CreateButton from 'components/CreateButton'

function EquipmentTable(props) {
  const [loading, setLoading] = useState(false)
  const [numRows, setNumRows] = useState(20)
  const [orderBy, order, getSorted, handleSort] = useTableSort(['code', 'DESC'])
  const [filters, setFilters] = useState({
    brandId: null,
    propertyId: null,
    locationId: null
  })

  const equipments = props.viewer.equipments.edges.map(({ node }) => node)

  useEffectAfterMount(() => {
    setLoading(true)
    const conditions = []

    const { brandId, propertyId, locationId } = filters

    if (brandId) conditions.push({ brandId: { eq: brandId } })
    if (propertyId) conditions.push({ propertyId: { eq: propertyId } })
    if (locationId) conditions.push({ locationId: { eq: locationId } })
    if (props.filterById) conditions.push(props.filterById)

    props.relay.refetchConnection(numRows, () => setLoading(false), {
      orderBy,
      order,
      where: conditions.length > 0 ? { and: conditions } : null,
    })
  }, [numRows, filters, orderBy, order])

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }


  return (
    <>
      {loading && (
        <Loading style={{ position: 'absolute', left: '50%', top: '1.5%' }} />
      )}

      <div style={{ textAlign: 'right' }}>
        <CreateButton
          can={CREATE_EQUIPMENT}
          path={`/equipment/create`}
        />
      </div>

      <Filters
        location={props.showLocationFilters}
        brand
        property
        numRows={numRows}
        setNumRows={setNumRows}
        setFilters={setFilters}
      >
        <BrandSelect
          value={filters.brandId}
          onChange={(value) => updateFilter('brandId', value)}
        />
        {props.showLocationFilters && (
          <PropertySelect
            value={filters.propertyId}
            onChange={(value) => updateFilter('propertyId', value)}
          />)}

      </Filters>

      <Table selectable sortable celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell
              content='Código'
              sorted={getSorted('code')}
              onClick={handleSort('code')}
            />
            <Table.HeaderCell
              content='Producto'
            />
            <Table.HeaderCell
              content='Familia'
            />
            <Table.HeaderCell
              content='Marca'
              sorted={getSorted('brand')}
              onClick={handleSort('brand')}
            />
            <Table.HeaderCell
              content='Modelo'
              sorted={getSorted('model')}
              onClick={handleSort('model')}
            />
            <Table.HeaderCell
              content='Número de Serie'
              sorted={getSorted('serialNumber')}
              onClick={handleSort('serialNumber')}
            />
            <Table.HeaderCell
              content='Propiedad'
              sorted={getSorted('property')}
              onClick={handleSort('property')}
            />
            <Table.HeaderCell
              content='Ubicación'
              sorted={getSorted('location')}
              onClick={handleSort('location')}
            />
            <Table.HeaderCell
              content='Estado'
              sorted={getSorted('status')}
              onClick={handleSort('status')}
            />
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {equipments.map(equipment => (
            <Table.Row
              key={equipment.id}
              onClick={_ => props.history.push(`/equipment/${equipment.id}`)}
            >
              <Table.Cell content={equipment.code} />
              <Table.Cell content={equipment.product.name} />
              <Table.Cell content={
                equipment.product && equipment.product.family && equipment.product.family.name
                  ? equipment.product.family.name
                  : "No disponible"
              } />
              <Table.Cell content={equipment.brand ? equipment.brand.name : ""} />
              <Table.Cell content={equipment.model} />
              <Table.Cell content={equipment.serialNumber} />
              <Table.Cell content={equipment.property ? equipment.property.name : ""} />
              <Table.Cell content={equipment.location ? equipment.location.name : ""} />
              <Table.Cell content={<EquipmentStatusLabel value={equipment.status} />} />
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  )
}

EquipmentTable.defaultProps = {
  showLocationFilters: true
}

export default createPaginationContainer(
  withRouter(EquipmentTable),
  {
    viewer: graphql`
      fragment EquipmentTable_viewer on Viewer
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 20 }
          cursor: { type: "String" }
          orderBy: { type: "[EquipmentFieldType]", defaultValue: [code] }
          order: { type: "OrderEnum", defaultValue: DESC }
          where: { type: "EquipmentWhere" }
        ) {
        equipments(
          first: $count
          after: $cursor
          orderBy: $orderBy
          order: $order
          where: $where
        ) @connection(key: "EquipmentTable_equipments") {
          edges {
            node {
              id
              code
              type
              status
              model
              serialNumber
              ip
              servicePorts
              installationDate
              notes
              brandId
              propertyId
              locationId
              createdAt
              createdBy
              updatedAt
              updatedBy
              brand {
                name
              }
              property {
                name
              }
              location {
                name
              }
              creator {
                name
              }
              updater {
                name
              }
              product {
                name
                family {
                  name
                }
              }
            }
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer && props.viewer.equipments
    },
    getFragmentVariables(prevVars, totalCount) {
      return { ...prevVars, count: totalCount }
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        count,
        cursor,
        orderBy: fragmentVariables.orderBy,
        order: fragmentVariables.order,
        where: fragmentVariables.where
      }
    },
    query: graphql`
      query EquipmentTableQuery(
        $count: Int!
        $cursor: String
        $orderBy: [EquipmentFieldType]
        $order: OrderEnum
        $where: EquipmentWhere
      ) {
        viewer {
          ...EquipmentTable_viewer
            @arguments(
              count: $count
              cursor: $cursor
              orderBy: $orderBy
              order: $order
              where: $where
            )
        }
      }
    `,
  }
)